
    // IMPORTS
    import { computed, defineComponent, onMounted, ref } from 'vue'
    import Moment from 'moment'
    import router from '@/router'

    import { buildDateFormatter, buildLocalization, getKendoShortDateFormat, OrderProposalApi } from '@/util'
    import { IOrderProposalDTO, useCartPriceCalculator } from '@/types/shoppingCart'
    import { NotificationDTO, useNotificationStore } from '@/store/notification'
    import { useSparePartsStore } from '@/store/spareParts'

    import { Button } from '@progress/kendo-vue-buttons'
    import { DialogActionsBar } from '@progress/kendo-vue-dialogs'
    import AppDialog from '@/components/common/app-dialog.vue'
    import { GridColumnProps } from '@progress/kendo-vue-grid'
    import { SortDescriptor } from '@progress/kendo-data-query'
    import { DatePicker } from '@progress/kendo-vue-dateinputs'

    import MyGrid from '@/components/grid/my-grid.vue'
    import OrderProposalUrlDialog from '@/components/shopping-cart/order-proposal-url-dialog.vue'
    import DateFilterCell from '@/components/grid/cells/date-filter-cell.vue'
    import Loader from '@/components/common/loader.vue'

    interface IOrderProposalRow {
        id: string,
        orderProposalNo: number,
        orderProposalNoFormatted: string,
        name: string,
        createdByUserName: string,
        createdLocal: Date,
        createdFormatted: string,
        expirationDateLocal: Date,
        expirationDateFormatted: string,
        expirationColor: string,
        lineItemCount: number,
        totalPriceText: string,
        isExpired: boolean
    }

    export default defineComponent({
        name: 'adminOrderProposalList',

        components: {
            MyGrid,
            'k-button': Button,
            AppDialog,
            DialogActionsBar,
            DatePicker,
            OrderProposalUrlDialog,
            Loader
        },

        setup() {
            /********************************************************************************************************************
             INIT
             ********************************************************************************************************************/
            const api = new OrderProposalApi()
            const { localize } = buildLocalization()
            const { computeTotalPriceText } = useCartPriceCalculator()
            const { formatUtcDate } = buildDateFormatter()
            const notificationStore = useNotificationStore()
            const sparePartsStore = useSparePartsStore()

            /********************************************************************************************************************
             PRIVATE DATA
             ********************************************************************************************************************/
            const proposals = ref<IOrderProposalDTO[] | null>(null)

            /********************************************************************************************************************
             PUBLIC DATA
             ********************************************************************************************************************/
            const extendDialogVisible = ref(false)
            const deleteDialogVisible = ref(false)
            const isProcessing = ref(false)
            const extendDate = ref<Date>(getDefaultExtendDate())
            const extendDateError = ref<string | null>(null)
            const selectedProposal = ref<IOrderProposalRow | null>(null)
            const urlDialogCartId = ref<string | null>(null)
            const urlDialogOrderProposalNo = ref<number>(0)
            const urlDialogName = ref<string>('')
            const kendoDateFormat = getKendoShortDateFormat()

            const publicData = {
                extendDialogVisible, deleteDialogVisible, isProcessing,
                extendDate, extendDateError, urlDialogCartId, urlDialogOrderProposalNo, urlDialogName,
                selectedProposal,
                kendoDateFormat
            }

            /********************************************************************************************************************
             COMPUTED DATA
             ********************************************************************************************************************/
            const initialSort = computed<SortDescriptor[]>(() => [{ field: 'orderProposalNoFormatted', dir: 'desc' }])

            const gridRows = computed<IOrderProposalRow[] | null>(() =>
                proposals.value?.map(p => toRow(p)) ?? null
            )

            const columns = computed(() => [
                {
                    field: 'orderProposalNoFormatted',
                    title: localize('ProposedOrder'),
                    width: 200,
                    cell: 'custom1'
                },
                {
                    field: 'name',
                    title: localize('Name'),
                    width: 180,
                },
                {
                    field: 'createdByUserName',
                    title: localize('CreatedBy'),
                    width: 150,
                },
                {
                    field: 'createdLocal',
                    title: localize('Created'),
                    filter: 'date',
                    filterCell: DateFilterCell,
                    width: 220,
                    cell: 'custom1'
                },
                {
                    field: 'expirationDateLocal',
                    title: localize('validUntil'),
                    filter: 'date',
                    filterCell: DateFilterCell,
                    width: 220,
                    cell: 'custom1'
                },
                {
                    field: 'lineItemCount',
                    title: localize('LineItems'),
                    width: 150,
                    filter: 'numeric',
                },
                {
                    field: 'totalPriceText',
                    title: localize('Total'),
                    minResizableWidth: 150,
                    cell: 'custom1'
                },
                {
                    field: 'actions',
                    title: ' ',
                    width: 50,
                    sortable: false,
                    filterable: false,
                    cell: 'custom1'
                }
            ] as GridColumnProps[])

            const pricesLoaded = computed(() => (sparePartsStore.spareParts?.length ?? 0) > 0)

            const minExtendDate = computed(() => {
                const tomorrow = new Date()
                tomorrow.setDate(tomorrow.getDate() + 1)
                tomorrow.setHours(0, 0, 0, 0)
                return tomorrow
            })

            const computedData = { initialSort, gridRows, columns, pricesLoaded, minExtendDate }

            /********************************************************************************************************************
             PUBLIC METHODS
             ********************************************************************************************************************/
            function showExtendDialog(row: IOrderProposalRow) {
                selectedProposal.value = row
                extendDate.value = getDefaultExtendDate()
                extendDateError.value = null
                extendDialogVisible.value = true
            }

            function closeExtendDialog() {
                extendDialogVisible.value = false
                selectedProposal.value = null
            }

            function onExtendDateChange(event: any) {
                extendDate.value = event.value
                validateExtendDate()
            }

            function onExtend() {
                if (!selectedProposal.value || !validateExtendDate()) return

                isProcessing.value = true
                api.extend(selectedProposal.value.id, {
                    newExpirationDate: extendDate.value.toISOString()
                })
                    .then(response => {
                        proposals.value = response.data
                        notificationStore.addNotification(
                            new NotificationDTO(localize('ValidityPeriodChanged'), 'success')
                        )
                        closeExtendDialog()
                    })
                    .finally(() => isProcessing.value = false)
            }

            function showDeleteConfirmation(row: IOrderProposalRow) {
                selectedProposal.value = row
                deleteDialogVisible.value = true
            }

            function closeDeleteDialog() {
                deleteDialogVisible.value = false
                selectedProposal.value = null
            }

            function onDelete() {
                if (!selectedProposal.value) return

                isProcessing.value = true
                api.delete(selectedProposal.value.id)
                    .then(response => {
                        proposals.value = response.data
                        notificationStore.addNotification(
                            new NotificationDTO(localize('ShoppingCartDeleted'), 'success')
                        )
                        closeDeleteDialog()
                    })
                    .finally(() => isProcessing.value = false)
            }

            function viewCart(row: IOrderProposalRow) {
                router.push({ name: 'shopping-cart-content', query: { id: row.id } })
            }

            function showUrlDialogFor(row: IOrderProposalRow) {
                urlDialogOrderProposalNo.value = row.orderProposalNo
                urlDialogName.value = row.name
                urlDialogCartId.value = row.id
            }

            const publicMethods = {
                localize,
                showExtendDialog, closeExtendDialog, onExtendDateChange, onExtend,
                showDeleteConfirmation, closeDeleteDialog, onDelete,
                viewCart, showUrlDialogFor
            }

            /********************************************************************************************************************
             PRIVATE METHODS
             ********************************************************************************************************************/
            function getDefaultExtendDate(): Date {
                const date = new Date()
                date.setDate(date.getDate() + 30)
                return date
            }

            function validateExtendDate(): boolean {
                const tomorrow = new Date()
                tomorrow.setDate(tomorrow.getDate() + 1)
                tomorrow.setHours(0, 0, 0, 0)

                if (extendDate.value < tomorrow) {
                    extendDateError.value = localize('ExpirationDateMustBeFuture')
                    return false
                }

                extendDateError.value = null
                return true
            }

            function toRow(proposal: IOrderProposalDTO): IOrderProposalRow {
                const expirationMoment = Moment.utc(proposal.expirationDate).local()
                const expirationLocal = expirationMoment.clone().startOf('day').toDate()
                const now = Moment()
                const daysUntilExpiration = expirationMoment.diff(now, 'days', true)

                let expirationColor = 'dot-green'
                if (daysUntilExpiration < 0) expirationColor = 'dot-red'
                else if (daysUntilExpiration <= 7) expirationColor = 'dot-yellow'

                return {
                    id: proposal.id,
                    orderProposalNo: proposal.orderProposalNo,
                    orderProposalNoFormatted: `JSP-${String(proposal.orderProposalNo).padStart(7, '0')}`,
                    name: proposal.name,
                    createdByUserName: proposal.createdByUserName,
                    createdLocal: Moment.utc(proposal.created).local().startOf('day').toDate(),
                    createdFormatted: formatUtcDate(proposal.created),
                    expirationDateLocal: expirationLocal,
                    expirationDateFormatted: formatUtcDate(proposal.expirationDate),
                    expirationColor,
                    lineItemCount: proposal.items.length,
                    totalPriceText: computeTotalPriceText(proposal.items),
                    isExpired: proposal.isExpired
                }
            }

            function loadProposals() {
                api.getAll()
                    .then(response => proposals.value = response.data)
            }

            /********************************************************************************************************************
             LIFE-CYCLE HOOKS
             ********************************************************************************************************************/
            onMounted(() => loadProposals())

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return { ...publicData, ...computedData, ...publicMethods }
        }
    });
